import {
    Alert,
    AlertTitle,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {InventoryItem} from "../../../../api/Logistics";
import React from "react";
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import moment from "moment";
import {projectLabel} from "../../../../api/Projects";
import {css} from "@emotion/css";
import {RowOptions2} from "../../purchasing/RowOptions";
import {RemoveDialog} from "./RemoveDialog";
import {grey, orange} from "@mui/material/colors";

export function ViewReservations(props: {
    onClose(): void;
    value: InventoryItem;
}) {
    const reservations = useAsync2(async (input) => {
        return api.logistics.getReservations(input)
    }, {
        inventoryItem: props.value.id,
    }, [props.value.id]);

    const pendingItems = useAsync2(async (input) => {
        return api.logistics.getUpcomingReservations(input)
    }, {
        inventoryItem: props.value.id,
    }, [props.value.id]);

    return (
        <Dialog2 open onClose={() => props.onClose()}>
            <DialogTitle>#{props.value.id} {props.value.name}{props.value.productCode ? ` (${props.value.productCode})` : null} Reservations</DialogTitle>
            <DialogContent style={{paddingLeft: 0, paddingRight: 0}}>
                {reservations.LoadingOrErrorElement}
                {pendingItems.error && pendingItems.LoadingOrErrorElement}

                <table className={tableCss}>
                    <thead>
                    <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Needed By</th>
                        <th>Qty</th>
                    </tr>
                    </thead>
                    <tbody>
                    {reservations.result?.data.length === 0 && <tr>
                        <td colSpan={4}>
                            <Alert severity="info">
                                <AlertTitle>No Reservations Yet</AlertTitle>
                                Project item reservations are automatically assigned 2 weeks prior to the Needed By date.
                                Reservations are calculated daily and when a reservation is manually updated by you.
                            </Alert>
                        </td>
                    </tr>}
                    {reservations.result?.data?.map((rsv) => (
                        <tr key={rsv.id.toString()} style={{
                            backgroundColor: rsv.wantQty > rsv.qty ? orange["100"] : undefined,
                        }}>
                            <td>
                                <RowOptions2
                                    options={[{
                                        name: "Remove",
                                        danger: true,
                                        onClick: (ctrl) => {
                                            ctrl.display(onClose => <RemoveDialog onClose={() => {
                                                onClose();
                                                reservations.reload();
                                            }} value={rsv} />)
                                            ctrl.onClose()
                                        },
                                    }]}
                                />
                            </td>
                            <td>
                                {rsv.inventoryRequest ? <>
                                    {projectLabel({projectNumber: rsv.projectNumber, name: rsv.projectName})} - {rsv.openingName || <i>No opening name</i>}
                                    <div style={{
                                        fontSize: "0.7rem",
                                    }}>
                                        {rsv.floor ? <div>{`Floor: ${rsv.floor}`}</div> : null}
                                        {rsv.updatedAt ? <div>{`Updated ${moment(rsv.updatedAt).format("MMM D [at] hh:mma")}`}</div> : null}
                                    </div>
                                </>: <>
                                    Manufacture #{rsv.manufacturePoNumber}
                                    <div style={{
                                        fontSize: "0.7rem",
                                    }}>
                                        {rsv.projectName ? <div>{`Project: ${projectLabel({projectNumber: rsv.projectNumber, name: rsv.projectName})} - ${rsv.openingName || "No opening name"}`}</div> : null}
                                        {rsv.floor ? <div>{`Floor: ${rsv.floor}`}</div> : null}
                                        {rsv.updatedAt ? <div>{`Updated ${moment(rsv.updatedAt).format("MMM D [at] hh:mma")}`}</div> : null}
                                    </div>
                                </>}
                            </td>
                            <td>
                                {rsv.neededBy ? <div>{`${moment(rsv.neededBy).fromNow()} (${moment(rsv.neededBy).format("MMM D, YYYY")})`}</div> : null}
                            </td>
                            <td style={{textAlign: "right"}}>
                                {rsv.qty}
                                {rsv.wantQty > rsv.qty && <div>(want {rsv.wantQty})</div>}
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td colSpan={4}>
                            <Divider />
                        </td>
                    </tr>
                    {pendingItems.result?.data.length === 0 && <tr>
                        <td colSpan={4}>
                            <Alert severity="info">
                                <AlertTitle>No Pending Reservations Yet</AlertTitle>
                                We'll show anything with a Needed By date in the next 6 weeks that hasn't been reserved here.
                            </Alert>
                        </td>
                    </tr>}
                    {pendingItems.result?.data.map(r => (<tr style={{
                        backgroundColor: grey["200"],
                    }}>
                        <td></td>
                        <td>
                            Pending: {projectLabel({projectNumber: r.projectNumber, name: r.projectName})} - {r.openingName || <i>No opening name</i>}
                            <div style={{
                                fontSize: "0.7rem",
                            }}>
                                {r.floor ? <div>{`Floor: ${r.floor}`}</div> : null}
                            </div>
                        </td>
                        <td>
                            {r.neededBy ? <div>{`${moment(r.neededBy).fromNow()} (${moment(r.neededBy).format("MMM D, YYYY")})`}</div> : null}
                        </td>
                        <td style={{textAlign: "right"}}>
                            {r.qty}
                        </td>
                    </tr>))}
                    </tbody>
                </table>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onClose()}>Close</Button>
            </DialogActions>
        </Dialog2>
    )
}

const tableCss = css({
    fontSize: "0.85rem",
    width: "100%",
    borderCollapse: "collapse",

    "& th": {
        textAlign: "left",
    },

    "& td:first-child": {
        paddingLeft: 16
    },
    "& td:last-child": {
        paddingRight: 16
    },
    "& td": {
        paddingLeft: 2,
        paddingRight: 2
    }
})